/**
 * The parts of HTML sanitizing that are the same whatever vocabulary you allow.
 *
 * Two sanitizers use these: `rich-text.ts` for the small Tiptap vocabulary the editors
 * emit, and `email-html.ts` for the much larger one real email arrives in. The
 * *allow-lists* differ and should; the escaping, the URL check, and the loop that removes
 * raw-content elements until the string settles must not — those are the parts where a
 * second, subtly different copy is how a hole gets in.
 *
 * Deliberately DOM-free: this runs in a Cloudflare Worker as well as a browser.
 */

/**
 * Elements whose *content* is not markup (script/style) or is parsed in a separate tree
 * (svg/math/template). Dropping only their tags would leave the payload behind as visible
 * text, so they are removed whole.
 */
export const RAW_CONTENT_ELEMENTS =
  /<(script|style|iframe|object|embed|noscript|template|svg|math|title|textarea)\b[^>]*>[\s\S]*?<\/\1\s*>/gi;

/** An `&` that already starts an entity, which must not be escaped again. */
const ENTITY = /^&(?:[a-z][a-z0-9]{1,30}|#\d{1,7}|#x[0-9a-f]{1,6});/i;

export const HREF = /\bhref\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/i;

/**
 * URL schemes an `href` may use.
 *
 * An allow-list, not a block-list, and that is the whole point: blocking `javascript:`
 * leaves `data:`, `vbscript:`, and every scheme a browser adds next.
 */
export const SAFE_HREF = /^(?:https?:|mailto:)/i;

export function escapeAttribute(value: string) {
  // Escaping an existing `&amp;` would grow the value on every pass, and both writes and
  // renders sanitize — so this has to stay idempotent.
  return value
    .replace(/&/g, (amp, index: number) => (ENTITY.test(value.slice(index)) ? amp : '&amp;'))
    .replace(/"/g, '&quot;');
}

export function readAttribute(attributes: string, pattern: RegExp) {
  const match = attributes.match(pattern);
  return match?.[1] ?? match?.[2] ?? match?.[3] ?? '';
}

/**
 * Removes comments and raw-content elements, repeating until the string stops changing.
 *
 * The loop is not belt-and-braces. Removing one element can reveal another that was split
 * across it — `<scr<script></script>ipt>` is a single pass away from becoming a live
 * script tag — so a one-shot replace is exactly the bug.
 */
export function stripRawContent(html: string) {
  let previous = html;
  let stripped = html.replace(/<!--[\s\S]*?-->/g, '').replace(RAW_CONTENT_ELEMENTS, '');
  while (stripped !== previous) {
    previous = stripped;
    stripped = stripped.replace(/<!--[\s\S]*?-->/g, '').replace(RAW_CONTENT_ELEMENTS, '');
  }
  return stripped;
}
